/**
 * drawingModes.js — per-mode precision and coordinate settings
 *
 * Every "domain" of drawing has different precision requirements.
 * This module defines them all. The active mode is stored in
 * st.drawingMode (defaults to "architectural").
 *
 * All internal coordinates stay in mm — only the SNAP and MERGE
 * thresholds change per mode. The UI shows units appropriate to the mode.
 */

export const DRAWING_MODES = {

  architectural: {
    id:          "architectural",
    label:       "Architectural",
    icon:        "🏠",
    unit:        "mm",
    worldSnap:   100,      // 100mm grid
    nodeMerge:   60,       // merge nodes within 60mm
    microEdge:   50,       // remove edges < 50mm
    minArea:     250000,   // 0.25 m² minimum face
    gridMinor:   100,      // minor grid line spacing (mm)
    gridMajor:   1000,     // major grid line spacing (mm)
    displayScale: v => `${Math.round(v)}mm`,
    displayArea:  a => `${(a/1e6).toFixed(2)}m²`,
    snapNode:    200,
    snapEdge:    80,
    snapMid:     120,
    snapIx:      100,
  },

  mechanical: {
    id:          "mechanical",
    label:       "Mechanical",
    icon:        "⚙",
    unit:        "mm",
    worldSnap:   0.1,      // 0.1mm grid (ISO engineering)
    nodeMerge:   0.05,     // merge within 0.05mm
    microEdge:   0.02,     // remove edges < 0.02mm
    minArea:     0.01,     // 0.01mm² minimum face
    gridMinor:   1,        // 1mm minor grid
    gridMajor:   10,       // 10mm major grid
    displayScale: v => `${v.toFixed(2)}mm`,
    displayArea:  a => `${a.toFixed(2)}mm²`,
    snapNode:    1,
    snapEdge:    0.5,
    snapMid:     0.8,
    snapIx:      0.5,
  },

  furniture: {
    id:          "furniture",
    label:       "Furniture / Millwork",
    icon:        "🪑",
    unit:        "mm",
    worldSnap:   1,        // 1mm grid
    nodeMerge:   0.5,
    microEdge:   0.5,
    minArea:     100,      // 100mm² minimum
    gridMinor:   10,
    gridMajor:   100,
    displayScale: v => `${Math.round(v)}mm`,
    displayArea:  a => `${(a/1000).toFixed(1)}cm²`,
    snapNode:    3,
    snapEdge:    1.5,
    snapMid:     2,
    snapIx:      1.5,
  },

  electrical: {
    id:          "electrical",
    label:       "Electrical / PCB",
    icon:        "⚡",
    unit:        "mm",
    worldSnap:   0.1,
    nodeMerge:   0.05,
    microEdge:   0.02,
    minArea:     0.01,
    gridMinor:   1,
    gridMajor:   10,
    displayScale: v => `${v.toFixed(2)}mm`,
    displayArea:  a => `${a.toFixed(2)}mm²`,
    snapNode:    0.5,
    snapEdge:    0.2,
    snapMid:     0.3,
    snapIx:      0.2,
  },

  civil: {
    id:          "civil",
    label:       "Civil / Site",
    icon:        "🌍",
    unit:        "m",
    worldSnap:   1000,     // 1m grid (coordinates in mm internally)
    nodeMerge:   500,
    microEdge:   200,
    minArea:     1e6,      // 1m² minimum
    gridMinor:   1000,
    gridMajor:   10000,
    displayScale: v => `${(v/1000).toFixed(2)}m`,
    displayArea:  a => `${(a/1e6).toFixed(1)}m²`,
    snapNode:    2000,
    snapEdge:    1000,
    snapMid:     1500,
    snapIx:      1000,
  },

  schematic: {
    id:          "schematic",
    label:       "Schematic / Diagram",
    icon:        "📐",
    unit:        "mm",
    worldSnap:   10,       // 10mm grid
    nodeMerge:   5,
    microEdge:   3,
    minArea:     50,
    gridMinor:   10,
    gridMajor:   100,
    displayScale: v => `${Math.round(v)}`,
    displayArea:  a => `${Math.round(a)}`,
    snapNode:    20,
    snapEdge:    8,
    snapMid:     12,
    snapIx:      10,
  },
};

export const DEFAULT_MODE = "architectural";

/** Get the active drawing mode config from state */
export function getMode(st) {
  return DRAWING_MODES[st.drawingMode || DEFAULT_MODE] || DRAWING_MODES.architectural;
}
